import type { CheckoutInteractionInput } from '../types/checkout';

declare const __CHECKOUT_BUILD__: string;

const MAX_TIMING_MS = 30 * 60 * 1000;

// Read once per page-open observation. Never send a raw URL, search, hash,
// credentials, or exception; no storage, SDK, network lookup or extra request.
export function checkoutSourceContext(): CheckoutInteractionInput['source_context'] {
  try {
    const client_occurred_at = new Date().toISOString();
    const raw = document.referrer;
    if (!raw) return { version: 1, referrer_status: 'EMPTY', client_occurred_at };
    const invalid = { version: 1 as const, referrer_status: 'INVALID' as const, client_occurred_at };
    if (raw.length > 8192 || /[\\\r\n\t]/.test(raw)) return invalid;
    let url: URL;
    try { url = new URL(raw); } catch { return invalid; }
    if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.origin.length > 320) return invalid;
    return {
      version: 1,
      referrer_origin: url.origin,
      referrer_status: url.origin === window.location.origin ? 'SAME_ORIGIN' : 'OBSERVED',
      client_occurred_at,
    };
  } catch {
    return { version: 1, referrer_status: 'UNAVAILABLE' };
  }
}

function boundedMilliseconds(value: number): number | undefined {
  if (!Number.isFinite(value)) return undefined;
  return Math.max(0, Math.min(MAX_TIMING_MS, Math.round(value)));
}

export function checkoutPageElapsedMS(): number | undefined {
  try {
    return boundedMilliseconds(globalThis.performance?.now());
  } catch {
    return undefined;
  }
}

export function checkoutElapsedSinceMS(startedAtMS: number | undefined): number | undefined {
  const current = checkoutPageElapsedMS();
  if (startedAtMS === undefined || current === undefined) return undefined;
  return boundedMilliseconds(current - startedAtMS);
}

export function checkoutNavigationMetrics(): Pick<
  CheckoutInteractionInput,
  'navigation_ttfb_ms' | 'dom_content_loaded_ms' | 'window_load_ms'
> {
  try {
    const navigation = globalThis.performance
      ?.getEntriesByType('navigation')
      ?.[0] as PerformanceNavigationTiming | undefined;
    if (!navigation) return {};

    const ttfb = boundedMilliseconds(navigation.responseStart - navigation.startTime);
    const domLoaded = navigation.domContentLoadedEventEnd > 0
      ? boundedMilliseconds(navigation.domContentLoadedEventEnd - navigation.startTime)
      : undefined;
    const windowLoaded = navigation.loadEventEnd > 0
      ? boundedMilliseconds(navigation.loadEventEnd - navigation.startTime)
      : undefined;

    return {
      ...(ttfb === undefined ? {} : { navigation_ttfb_ms: ttfb }),
      ...(domLoaded === undefined ? {} : { dom_content_loaded_ms: domLoaded }),
      ...(windowLoaded === undefined ? {} : { window_load_ms: windowLoaded }),
    };
  } catch {
    return {};
  }
}

function viewportBucket(width: number): CheckoutInteractionInput['viewport_bucket'] {
  if (width < 360) return 'lt360';
  if (width < 480) return '360_479';
  if (width < 768) return '480_767';
  if (width < 1024) return '768_1023';
  return 'gte1024';
}

export function checkoutClientContext(): Pick<
  CheckoutInteractionInput,
  | 'device_class'
  | 'os_family'
  | 'browser_family'
  | 'connection_type'
  | 'viewport_bucket'
  | 'frontend_build'
> {
  try {
    const ua = navigator.userAgent.toLowerCase();
    const width = Math.max(0, window.innerWidth || document.documentElement.clientWidth || 0);
    const isIPadDesktopUA = /macintosh/.test(ua) && navigator.maxTouchPoints > 1;
    const isTablet = isIPadDesktopUA || /ipad|tablet|kindle|silk/.test(ua) || (/android/.test(ua) && !/mobile/.test(ua));
    const isMobile = !isTablet && (/mobile|iphone|ipod|android/.test(ua) || (width > 0 && width < 768));

    let osFamily: NonNullable<CheckoutInteractionInput['os_family']> = 'other';
    if (/android/.test(ua)) osFamily = 'android';
    else if (isIPadDesktopUA || /iphone|ipad|ipod/.test(ua)) osFamily = 'ios';
    else if (/windows/.test(ua)) osFamily = 'windows';
    else if (/macintosh|mac os/.test(ua)) osFamily = 'macos';
    else if (/linux/.test(ua)) osFamily = 'linux';

    let browserFamily: NonNullable<CheckoutInteractionInput['browser_family']> = 'other';
    if (/samsungbrowser/.test(ua)) browserFamily = 'samsung';
    else if (/edg\//.test(ua)) browserFamily = 'edge';
    else if (/firefox|fxios/.test(ua)) browserFamily = 'firefox';
    else if (/chrome|crios/.test(ua)) browserFamily = 'chrome';
    else if (/safari/.test(ua)) browserFamily = 'safari';

    const connection = (navigator as Navigator & {
      connection?: { effectiveType?: string };
    }).connection;
    const rawConnection = connection?.effectiveType;
    const connectionType = rawConnection === 'slow-2g' || rawConnection === '2g' ||
      rawConnection === '3g' || rawConnection === '4g'
      ? rawConnection
      : 'unknown';
    const frontendBuild = typeof __CHECKOUT_BUILD__ !== 'undefined' ? __CHECKOUT_BUILD__ : 'unknown';

    return {
      device_class: isTablet ? 'tablet' : isMobile ? 'mobile' : width > 0 ? 'desktop' : 'unknown',
      os_family: osFamily,
      browser_family: browserFamily,
      connection_type: connectionType,
      ...(width > 0 ? { viewport_bucket: viewportBucket(width) } : {}),
      frontend_build: /^[A-Za-z0-9._-]{1,64}$/.test(frontendBuild) ? frontendBuild : 'unknown',
    };
  } catch {
    return {
      device_class: 'unknown',
      os_family: 'other',
      browser_family: 'other',
      connection_type: 'unknown',
      frontend_build: 'unknown',
    };
  }
}
